import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Avatar, Stack, Text } from '@repletoryreach/sdk-ui';
import { useT } from '../../i18n/I18nProvider';
import './ProfileMenu.css';
import { disablePush } from '../../lib/push';

/**
 * Account menu in the header.
 *
 * Ported from `apps/admin-ui/src/app/components/ProfileMenu.tsx` per
 * `_UI_improvements/tenant/_app-shell.md` §4 item 11. The previous tenant
 * implementation put `role="menu"` on a plain `<div>` whose children were bare
 * `<button>`s: no `role="menuitem"`, no arrow-key handling, no Escape, no focus
 * return, and no `aria-expanded`/`aria-haspopup` on the trigger. The dismiss
 * scrim was click-only, so a keyboard user could open it and never close it.
 *
 * This version is a real menu: portal-rendered so it escapes the header's
 * stacking context, dismissed by outside click / Escape / scroll, navigable
 * with arrow keys, and it returns focus to the trigger on close.
 */
export function ProfileMenu() {
  const t = useT();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [opened, setOpened] = useState(false);
  const [coords, setCoords] = useState<{ top: number; right: number } | null>(null);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const name = user?.display_name ?? user?.email ?? t('shell.profile.guest');
  const initials = (user?.display_name ?? user?.email ?? '?').slice(0, 1).toUpperCase();

  // Right-align the dropdown under the trigger.
  useEffect(() => {
    if (!opened || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setCoords({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, [opened]);

  // Dismiss on outside click, Escape and scroll; return focus to the trigger.
  useEffect(() => {
    if (!opened) return;
    function onPointer(e: MouseEvent) {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setOpened(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpened(false);
        anchorRef.current?.focus();
        return;
      }
      // Roving focus across the menu items.
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const items = dropdownRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]');
        if (!items?.length) return;
        e.preventDefault();
        const list = Array.from(items);
        const at = list.indexOf(document.activeElement as HTMLButtonElement);
        const next = e.key === 'ArrowDown'
          ? list[(at + 1) % list.length]
          : list[(at - 1 + list.length) % list.length];
        next?.focus();
      }
    }
    function onScroll() { setOpened(false); }

    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [opened]);

  // Focus the first item when the menu opens by keyboard.
  useEffect(() => {
    if (!opened) return;
    const first = dropdownRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]');
    first?.focus();
  }, [opened, coords]);

  function go(path: string) {
    setOpened(false);
    navigate(path);
  }

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={opened}
        aria-label={t('shell.profile.open')}
        className="rr-profile-menu__trigger"
        onClick={() => setOpened((v) => !v)}
      >
        <Avatar size="sm" radius="xl" className="rr-profile-menu__avatar">{initials}</Avatar>
        <span className="rr-profile-menu__name">{name}</span>
        <Chevron opened={opened} />
      </button>

      {opened && coords && createPortal(
        <div
          ref={dropdownRef}
          role="menu"
          aria-label={t('shell.profile.menu')}
          className="rr-profile-menu__dropdown"
          style={{ top: coords.top, right: coords.right }}
        >
          <div className="rr-profile-menu__summary">
            <Stack gap={2}>
              <Text role="bodySmall" fw={700}>{user?.display_name ?? user?.email}</Text>
              {user?.display_name && (
                <Text role="caption" c="var(--rr-color-text-muted)">{user.email}</Text>
              )}
              {user?.org?.name && (
                <Text role="caption" c="var(--rr-color-text-muted)">{user.org.name}</Text>
              )}
            </Stack>
          </div>

          <div className="rr-profile-menu__divider" role="separator" />

          <MenuItem icon={<UserIcon />} onClick={() => go('/settings/profile')}>
            {t('settings.layout.nav.profile', 'Profile settings')}
          </MenuItem>
          <MenuItem icon={<ShieldIcon />} onClick={() => go('/settings/security')}>
            {t('settings.layout.nav.security', 'Security')}
          </MenuItem>
          <MenuItem icon={<WalletIcon />} onClick={() => go('/billing')}>
            {t('nav.billing')}
          </MenuItem>
          <MenuItem icon={<CogIcon />} onClick={() => go('/settings')}>
            {t('shell.profile.allSettings')}
          </MenuItem>

          <div className="rr-profile-menu__divider" role="separator" />

          <MenuItem
            tone="error"
            icon={<LogoutIcon />}
            onClick={async () => {
              setOpened(false);
              // This browser stops getting this person's alerts before the session ends (9 October 2026).
              await disablePush();
              await logout();
              navigate('/auth/login', { replace: true });
            }}
          >
            {t('action.signOut')}
          </MenuItem>
        </div>,
        document.body
      )}
    </>
  );
}

interface MenuItemProps {
  icon?: ReactNode;
  onClick: () => void;
  tone?: 'default' | 'error';
  children: ReactNode;
}
function MenuItem({ icon, onClick, tone = 'default', children }: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      className={`rr-profile-menu__item rr-profile-menu__item--${tone}`}
      onClick={onClick}
    >
      {icon && <span className="rr-profile-menu__item-icon">{icon}</span>}
      <span className="rr-profile-menu__item-label">{children}</span>
    </button>
  );
}

function Chevron({ opened }: { opened: boolean }) {
  return (
    <svg
      width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden
      className={`rr-profile-menu__chevron${opened ? ' is-open' : ''}`}
    >
      <polyline points="3,5 6,8 9,5" />
    </svg>
  );
}

function Icon({ children }: { children: ReactNode }) {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {children}
    </svg>
  );
}
function UserIcon() { return <Icon><path d="M8 8a3 3 0 100-6 3 3 0 000 6z" /><path d="M3 14a5 5 0 0110 0" /></Icon>; }
function ShieldIcon() { return <Icon><path d="M8 1.5l5.5 2.4v3.6c0 4-5.5 7-5.5 7s-5.5-3-5.5-7V3.9L8 1.5z" /></Icon>; }
function WalletIcon() { return <Icon><rect x="2" y="4" width="12" height="8" rx="1.5" /><path d="M2 6.5h12" /><circle cx="11.5" cy="9.5" r="0.75" fill="currentColor" /></Icon>; }
function CogIcon() { return <Icon><circle cx="8" cy="8" r="2" /><path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M3.4 12.6l1.4-1.4M11.2 4.8l1.4-1.4" /></Icon>; }
function LogoutIcon() { return <Icon><path d="M9.5 5V3.5a1 1 0 00-1-1h-5a1 1 0 00-1 1v9a1 1 0 001 1h5a1 1 0 001-1V11" /><path d="M7 8h7M11.5 5.5L14 8l-2.5 2.5" /></Icon>; }
